import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Eye, TrendingUp, UserPlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Replaces ReelsStatsSection + ViewsComparisonSection + FollowerGrowthSection —
// same three proof points (total views, before/after growth, new followers),
// compressed into one section that fits a single mobile screen instead of three.

const STORAGE_BASE = "https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets";

const STATS = [
  { icon: Eye, value: 76, suffix: "M+", label: "wyświetleń wygenerowanych" },
  { icon: TrendingUp, value: 5797, suffix: "%", label: "rekordowy wzrost przed/po" },
  { icon: UserPlus, value: 2222, suffix: "+", label: "obserwujących z jednej rolki" },
] as const;

// Only portrait/near-square source screenshots — the follower-count assets
// (jak_pozyskac_obserwatorow*.png) are wide 3:1 stat rows that crop badly
// in a portrait card, so that data point stays represented via the stat
// tile above instead of a cropped screenshot here.
const PROOF_SHOTS = [
  {
    src: `${STORAGE_BASE}/wieksze_zasiegi_instagram1.png`,
    badge: "4,5 mln wyświetleń",
    alt: "Reel z wynikiem 4,5 miliona wyświetleń",
  },
  {
    src: `${STORAGE_BASE}/jak_zwiekszyc_wyswietlenia2.png`,
    badge: "+5797% wyświetleń",
    alt: "Porównanie przed i po — wzrost o 5797% wyświetleń",
  },
  {
    src: `${STORAGE_BASE}/wieksze_zasiegi_instagram2.png`,
    badge: "695 tys. wyświetleń",
    alt: "Reel z wynikiem 695 tysięcy wyświetleń",
  },
  {
    src: `${STORAGE_BASE}/jak_zwiekszyc_wyswietlenia1.png`,
    badge: "+1532% wyświetleń",
    alt: "Porównanie przed i po — wzrost o 1532% wyświetleń",
  },
] as const;

const GLASS_TILE_STYLE = {
  background: "rgba(255, 255, 255, 0.12)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
};

interface AnimatedNumberProps {
  value: number;
  suffix: string;
  duration?: number;
}

const AnimatedNumber = ({ value, suffix, duration = 1.6 }: AnimatedNumberProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.floor(eased * value));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, value, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {displayValue.toLocaleString("pl-PL")}
      {suffix}
    </span>
  );
};

const CondensedStatsSection = () => {
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const activeShot = lightboxIndex !== null ? PROOF_SHOTS[lightboxIndex] : null;

  return (
    <section id="porownanie" className="relative py-14 md:py-24 overflow-hidden scroll-mt-24">
      {/* Single gradient spanning the whole section — replaces the 3 separate
          blue → peach → orange handoffs the old sections used. */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0080FF] via-[#4a97ec] to-[#F5A07A]" />
      <div
        className="absolute inset-x-0 top-0 h-32 md:h-40 pointer-events-none"
        style={{ background: "linear-gradient(to bottom, #FEF3EC, transparent)" }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="text-center mb-6 md:mb-12"
        >
          <p className="text-sm font-semibold tracking-widest uppercase text-white/80 mb-2">
            Wyniki naszych twórców
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white">
            Liczby, które <span className="text-orange-400">mówią same za siebie.</span>
          </h2>
        </motion.div>

        {/* Stat tiles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 100, damping: 20, delay: 0.1 }}
          className="grid grid-cols-3 gap-2.5 md:gap-6 mb-6 md:mb-12"
        >
          {STATS.map(({ icon: Icon, value, suffix, label }) => (
            <div
              key={label}
              className="rounded-2xl p-3 md:p-6 text-center"
              style={GLASS_TILE_STYLE}
            >
              <Icon className="w-4 h-4 md:w-7 md:h-7 text-white/90 mx-auto mb-1.5 md:mb-2" strokeWidth={2} />
              <div className="text-xl md:text-4xl font-bold text-white">
                <AnimatedNumber value={value} suffix={suffix} />
              </div>
              <p className="text-[10px] md:text-sm text-white/75 mt-1 leading-snug">{label}</p>
            </div>
          ))}
        </motion.div>

        {/* Proof strip — horizontal scroll on mobile, grid on desktop.
            Each shot opens a lightbox with the full-size image on click. */}
        <div className="proof-strip flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-4 md:gap-6">
          {PROOF_SHOTS.map((shot, i) => (
            <button
              key={shot.src}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className="relative flex-shrink-0 w-[126px] sm:w-[150px] md:w-auto snap-start rounded-2xl overflow-hidden shadow-xl ring-1 ring-white/10 text-left cursor-zoom-in transition-transform hover:scale-[1.03] active:scale-[0.98]"
              aria-label={`Zobacz statystyki — ${shot.badge}`}
            >
              <img
                src={shot.src}
                alt={shot.alt}
                className="w-full h-[170px] sm:h-[200px] md:h-[220px] object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 to-transparent p-2 pt-6">
                <span className="text-white text-[10px] md:text-xs font-bold leading-tight">
                  {shot.badge}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Lightbox — tap a shot to see the full stat screenshot */}
        <Dialog open={activeShot !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
          <DialogContent className="max-w-2xl w-[calc(100%-2rem)] bg-transparent border-none shadow-none p-0 gap-0">
            <DialogTitle className="sr-only">{activeShot?.badge}</DialogTitle>
            {activeShot && (
              <div className="relative rounded-2xl overflow-hidden bg-gray-900 shadow-2xl">
                <img
                  src={activeShot.src}
                  alt={activeShot.alt}
                  className="w-full max-h-[80vh] object-contain bg-white"
                />
                <div className="bg-gray-900 px-4 py-3 text-center">
                  <span className="text-white text-sm md:text-base font-bold">{activeShot.badge}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setLightboxIndex(null)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                  aria-label="Zamknij"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-center mt-6 md:mt-12"
        >
          <button
            type="button"
            onClick={() => navigate("/auth?mode=register&trial=true")}
            className="relative overflow-hidden group inline-flex items-center justify-center gap-2 px-6 py-3.5 md:px-8 md:py-4 bg-orange-500 text-white font-bold text-sm md:text-lg rounded-full shadow-xl shadow-orange-500/25 hover:bg-orange-600 hover:shadow-2xl active:scale-[0.98] transition-all duration-300"
          >
            <span
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%)",
                backgroundSize: "200% 100%",
                animation: "shimmer-sweep 4s ease-in-out infinite",
              }}
            />
            <span className="relative z-10 inline-block transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">⚡</span>
            <span className="relative z-10">Chcę takie wyniki</span>
          </button>
        </motion.div>
      </div>

      <style>{`
        .proof-strip::-webkit-scrollbar { display: none; }
        .proof-strip { scrollbar-width: none; }
        @keyframes shimmer-sweep {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </section>
  );
};

export default CondensedStatsSection;
